import {ChecklistTemplateSummary, Column, Task} from '@/interfaces/TaskInterfaces';
import {Module} from 'vuex';
import {UserRoleEnum} from '@/enums/userRolesEnum';
import {sortTask} from "@/utils/taskUtil";

interface UserTasks {
  tasks: Task[];
  todaysTasks: Task[];
  weekTasks: Task[];
  incomingTasks: Task[];
  laterTasks: Task[];
  waitingTasks: Task[];
  completedTasks: Task[];
  waitingForData: boolean
}

interface State {
  userIdInFocus: number
  individualTaskIds: number[],
  individualTasks: Task[],
  delegatedTasks: Task[],
  watchDelegated: boolean
  checklistTemplates: ChecklistTemplateSummary[],
  userToTasks: Map<number, UserTasks>
}

const state: State = {
  userIdInFocus: 0,
  individualTaskIds: [],
  individualTasks: [],
  delegatedTasks: [],
  watchDelegated: false,
  checklistTemplates: [],
  userToTasks: new Map<number, UserTasks>()
};

export default {
  namespaced: true,
  state,
  getters: {
    getTasksForUserId: (state) => (userId: number) => {
      return state.userToTasks.get(userId) || {
        tasks: [],
        todaysTasks: [],
        weekTasks: [],
        incomingTasks: [],
        laterTasks: [],
        waitingTasks: [],
        completedTasks: [],
        waitingForData: true
      };
    }
  },
  actions: {
    addTask({ commit, state, rootState }, task: Task) {
      commit('addTask', { task, roles: rootState.user.roles, appUserId: rootState.user.appUserId });
    },
    updateTask({ commit, state, rootState }, task: Task) {
      commit('deleteTask', task.id);
      commit('addTask', { task, roles: rootState.user.roles, appUserId: rootState.user.appUserId });
    },
    deleteTask({ commit, state, rootState }, id: number) {
      commit('deleteTask', id);
    },
    reorderTasks({ commit, state, rootState }, reorderData: any) {
      if (!reorderData.assignedToAppUserId || reorderData.assignedToAppUserId === rootState.user.appUserId) {
        // if the data is reordering the same user that is logged in, the client already calculated the reordering out
        // this is skipped to prevent screen flickering when multiple drag and drop actions occur where the second one
        // overrides the order of the first.
        return;
      }
      const userToReorder = reorderData.assignedToAppUserId
        ? reorderData.assignedToAppUserId
        : rootState.user.appUserId;
      const userTasks : UserTasks = state.userToTasks.get(userToReorder)!;
      if (!userTasks) {
        return;
      }
      let listToReorder = [] as Task[];
      switch (reorderData.taskColumnId) {
        case Column.INCOMING:
          listToReorder = userTasks.incomingTasks;
          break;
        case Column.TODAY:
          listToReorder = userTasks.todaysTasks;
          break;
        case Column.THIS_WEEK:
          listToReorder = userTasks.weekTasks;
          break;
        case Column.LATER:
          listToReorder = userTasks.laterTasks;
          break;
        case Column.WAITING:
          listToReorder = userTasks.waitingTasks;
          break;
        case Column.COMPLETE:
          listToReorder = userTasks.completedTasks;
          break;
      }

      listToReorder.forEach((task : Task, index : number) => {
        const reorderForTask = reorderData.orderings.filter((ordering: any) => {
          return ordering.taskId === task.id;
        })[0];
        if (reorderForTask) {
          if (reorderForTask.displayOrder !== task.displayOrder) {
            task.displayOrder = reorderForTask.displayOrder;
            commit('deleteTask', task.id);
            commit('addTask', { task, roles: rootState.user.roles, appUserId: rootState.user.appUserId });
          }
        }
      });
      if (reorderData.updatedTask) {
        commit('deleteTask', reorderData.updatedTask.id);
        commit('addTask', { task:reorderData.updatedTask, roles: rootState.user.roles, appUserId: rootState.user.appUserId });
      }
    }
  },
  mutations: {
    setUserIdInFocus(state: State, userIdInFocus: number) {
      state.userIdInFocus = userIdInFocus;
    },
    watchDelegated(state: State, watchDelegated: boolean) {
      state.watchDelegated = watchDelegated;
    },
    setDelegatedTasks(state: State, tasks: Task[]) {
      if (state.watchDelegated) {
        state.delegatedTasks = tasks;
      } else {
        state.delegatedTasks = [];
      }
    },
    addIndividualId(state: State, taskId: number) {
      if (!state.individualTaskIds.includes(taskId)) {
        state.individualTaskIds.push(taskId);
      }
    },
    removeIndividualId(state: State, taskId: number) {
      state.individualTaskIds = state.individualTaskIds.filter(id => id !== taskId);
      state.individualTasks = state.individualTasks.filter(task => task.id !== taskId);
    },
    individualTaskUpdated(state: State, task: Task) {
      const index = state.individualTasks.findIndex(possible => possible.id === task.id);
      if (index > -1) {
        state.individualTasks.splice(index, 1, task);
      } else {
        state.individualTasks.push(task);
      }
    },
    setTasks(state: State, data: {tasks: Task[], userId: number}) {
      const userTasks: UserTasks = {
        tasks: [],
        todaysTasks: [],
        weekTasks: [],
        incomingTasks: [],
        laterTasks: [],
        waitingTasks: [],
        completedTasks: [],
        waitingForData: false
      };
      userTasks!.tasks = data.tasks;
      const todaysTasks = [];
      const weekTasks = [];
      const incomingTasks = [];
      const laterTasks = [];
      const waitingTasks = [];
      const completedTasks = [];
      for (const task of data.tasks) {
        (task as any).isEditing = false;
        if (task.isComplete) {
          completedTasks.push(task);
        } else if (task.taskColumnId === Column.INCOMING) {
          incomingTasks.push(task);
        } else if (task.taskColumnId === Column.TODAY) {
          todaysTasks.push(task);
        } else if (task.taskColumnId === Column.THIS_WEEK) {
          weekTasks.push(task);
        } else if (task.taskColumnId === Column.LATER) {
          laterTasks.push(task);
        } else if (task.taskColumnId === Column.WAITING) {
          waitingTasks.push(task);
        }
      }
      userTasks.tasks.sort(sortTask);
      userTasks.todaysTasks = todaysTasks.sort(sortTask);
      userTasks.weekTasks = weekTasks.sort(sortTask);
      userTasks.incomingTasks = incomingTasks.sort(sortTask);
      userTasks.laterTasks = laterTasks.sort(sortTask);
      userTasks.waitingTasks = waitingTasks.sort(sortTask);
      userTasks.completedTasks = completedTasks.sort(sortTask);
      state.userToTasks.set(data.userId, userTasks);
      triggerReactiveUpdateOnMap(state);
    },
    addTask(state: State, data: { task: Task, roles: UserRoleEnum[], appUserId: number }) {
      const task = data.task;
      if (!state.userToTasks.get(data.appUserId)) {
        return;
      }
      const userTasks = state.userToTasks.get(task.assignedToAppUserId!);
      if (userTasks) {
        (task as any).isEditing = false;
        addTaskToUserTasks(userTasks, task);
      } else {
        const loggedInUserTasks = state.userToTasks.get(data.appUserId);
        if (loggedInUserTasks && data.roles.find((role: any) => role.id === task.assignedToSecurityRoleId)) {
          (task as any).isEditing = false;
          addTaskToUserTasks(loggedInUserTasks, task);
        }
      }
      triggerReactiveUpdateOnMap(state);
    },
    deleteTask(state: State, id: number) {
      state.userToTasks.forEach((userTasks: UserTasks, key: number) => {
        let filterById = (id: number) => {
          return (aTask: Task) => aTask.id !== id;
        };
        if (userTasks) {
          userTasks.tasks = userTasks.tasks.filter(filterById(id));
          userTasks.completedTasks = userTasks.completedTasks.filter(filterById(id));
          userTasks.incomingTasks = userTasks.incomingTasks.filter(filterById(id));
          userTasks.todaysTasks = userTasks.todaysTasks.filter(filterById(id));
          userTasks.weekTasks = userTasks.weekTasks.filter(filterById(id));
          userTasks.laterTasks = userTasks.laterTasks.filter(filterById(id));
          userTasks.waitingTasks = userTasks.waitingTasks.filter(filterById(id));
        }
      });
      triggerReactiveUpdateOnMap(state);
    },
    setTemplates(state: State, templates: ChecklistTemplateSummary[]) {
      state.checklistTemplates = templates;
    },
  }
} as Module<State, any>;

/**
 * Hacky function to work around the fact that vuex doesn't react to map updates.  By shallow copying the map itself it works.
 */
function triggerReactiveUpdateOnMap(state: State) {
  state.userToTasks = new Map(state.userToTasks);
}

function addTaskToUserTasks(userTasks: UserTasks, task: Task) {
  userTasks.tasks = userTasks.tasks.concat(task);
  userTasks.tasks.sort(sortTask);
  if (task.isComplete) {
    userTasks.completedTasks = userTasks.completedTasks.concat(task);
    userTasks.completedTasks.sort(sortTask);
  } else if (task.taskColumnId === Column.INCOMING) {
    userTasks.incomingTasks = userTasks.incomingTasks.concat(task);
    userTasks.incomingTasks.sort(sortTask);
  } else if (task.taskColumnId === Column.TODAY) {
    userTasks.todaysTasks = userTasks.todaysTasks.concat(task);
    userTasks.todaysTasks.sort(sortTask);
  } else if (task.taskColumnId === Column.THIS_WEEK) {
    userTasks.weekTasks = userTasks.weekTasks.concat(task);
    userTasks.weekTasks.sort(sortTask);
  } else if (task.taskColumnId === Column.LATER) {
    userTasks.laterTasks = userTasks.laterTasks.concat(task);
    userTasks.laterTasks.sort(sortTask);
  } else if (task.taskColumnId === Column.WAITING) {
    userTasks.waitingTasks = userTasks.waitingTasks.concat(task);
    userTasks.waitingTasks.sort(sortTask);
  }
}



// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--0!./src/store/modules/tasks.ts